import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, FlexBox, Icon, IconButton, Tooltip, } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import snakeCase from 'lodash/snakeCase';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useSetState, useUpdateEffect } from 'react-use';
import { v4 as uuidv4 } from 'uuid';
import { HookFormSwitch, HookFormText } from 'components/hookForm';
import { getObjectByPath } from 'utils/getObjectByPath';
import { useAppModal } from 'utils/modal';
import { convertTreeToFlatItems, treeFind } from 'utils/tree';
import { useOrderedDragDrop, } from 'utils/useOrderedDragDrop';
import { optionOrderIndexStep } from '../../optionOrderIndexStep';
import { optionsKey } from '../../optionsKey';
import { Options } from '../options/options';
import styles from './option.module.scss';
/** Represents an option in the options form field for custom select attribute. */
export const Option = memo(function Option({ fieldIndex, isLastOption, isParentDeactivated, isValueChangedByUser, orderIndex, parentName, onMove, onRemove, onUserInput, }) {
    var _a, _b, _c;
    const { modal } = useAppModal('settings:customAttribute');
    const rowRef = useRef(null);
    const optionRef = useRef(null);
    const rowHandleRef = useRef(null);
    // TODO: [TEAMS-13693] add `AttributeFormFieldValuesHavingOptions` generic back after updating to RHF v8
    const { control, formState, setValue, trigger, getValues } = useFormContext();
    const { errors, dirtyFields } = formState;
    const optionFormName = parentName
        ? `${parentName}.${optionsKey}.${fieldIndex}`
        : `${optionsKey}.${fieldIndex}`;
    const idFormName = `${optionFormName}.id`;
    const labelFormName = `${optionFormName}.label`;
    const valueFormName = `${optionFormName}.value`;
    const optionsFormName = `${optionFormName}.${optionsKey}`;
    const deactivatedFormName = `${optionFormName}.is_deactivated`;
    const { fields, append, remove } = useFieldArray({
        control,
        name: optionsFormName,
    });
    const optionId = useMemo(() => getValues(idFormName), [getValues, idFormName]);
    const [{ label: optionLabel, value: optionValue }, setOptionValues] = useSetState({
        label: (_a = getValues(labelFormName)) !== null && _a !== void 0 ? _a : '',
        value: (_b = getValues(valueFormName)) !== null && _b !== void 0 ? _b : '',
    });
    const hasOptions = !!fields.length;
    const [isOptionsExpanded, setIsOptionsExpanded] = useState(false);
    const optionFromEndpoint = useMemo(() => {
        if ((modal === null || modal === void 0 ? void 0 : modal.arg.mode) === 'edit' && modal.arg.attribute.type === 'select') {
            return treeFind(modal.arg.attribute.options, ({ id }) => optionId === id, 'options');
        }
    }, [modal, optionId]);
    const hasOptionsFromEndpoint = !!((_c = optionFromEndpoint === null || optionFromEndpoint === void 0 ? void 0 : optionFromEndpoint.options) === null || _c === void 0 ? void 0 : _c.length);
    const optionErrors = getObjectByPath(errors, optionFormName);
    const optionDirtyFields = getObjectByPath(dirtyFields, optionFormName);
    const isOptionFromEndpoint = !!optionFromEndpoint;
    const isOptionBlank = !optionLabel && !optionValue;
    const isOptionValid = !(optionErrors === null || optionErrors === void 0 ? void 0 : optionErrors.label) && !(optionErrors === null || optionErrors === void 0 ? void 0 : optionErrors.value);
    const isOptionsValid = !(optionErrors === null || optionErrors === void 0 ? void 0 : optionErrors.options);
    const isOptionDeactivated = useWatch({ control, name: deactivatedFormName });
    const isInputRequired = hasOptions || !isOptionBlank;
    const showAlert = isInputRequired && !isLastOption && !isOptionValid;
    const { isDragging, isOver } = useOrderedDragDrop({
        type: parentName || 'options',
        index: orderIndex,
        source: rowHandleRef,
        target: rowRef,
        preview: optionRef,
        onMove,
        triggerSwap: 'drop',
    });
    /** Handles text input. */
    const handleInput = useCallback((field, event) => {
        setOptionValues({ [field]: event.target.value });
        if (!isOptionFromEndpoint && field === 'label' && !isValueChangedByUser) {
            const label = event.target.value;
            setValue(valueFormName, snakeCase(label), {
                shouldTouch: true,
                shouldDirty: true,
            });
        }
        if (optionErrors === null || optionErrors === void 0 ? void 0 : optionErrors.value) {
            trigger('options');
        }
        onUserInput({ index: fieldIndex, field });
    }, [
        fieldIndex,
        isOptionFromEndpoint,
        isValueChangedByUser,
        optionErrors,
        valueFormName,
        onUserInput,
        setOptionValues,
        setValue,
        trigger,
    ]);
    /** Handles input blur. */
    const handleBlur = useCallback((field, event) => {
        if (field === 'value' && (optionDirtyFields === null || optionDirtyFields === void 0 ? void 0 : optionDirtyFields.value) === true) {
            trigger('options');
        }
    }, [trigger, optionDirtyFields]);
    /** Handles input focus. */
    const handleFocus = useCallback((field, event) => {
        if (field === 'value' && !isValueChangedByUser) {
            trigger('options');
        }
    }, [trigger, isValueChangedByUser]);
    /** Handles creating nested options under the current option. */
    const createTree = useCallback(() => {
        setIsOptionsExpanded(true);
        if (isOptionBlank) {
            // We do this so we can make the field touched so that we can show the warning
            setValue(optionFormName, getValues(optionFormName), {
                shouldTouch: true,
            });
        }
        append({
            id: uuidv4(),
            label: '',
            value: '',
            is_deactivated: isOptionDeactivated,
            order_index: optionOrderIndexStep,
            parent_id: optionId,
        }, { shouldFocus: false });
    }, [
        isOptionBlank,
        isOptionDeactivated,
        optionFormName,
        optionId,
        append,
        getValues,
        setValue,
    ]);
    // Propagate active state change to children
    useUpdateEffect(() => {
        const options = getValues(optionsFormName);
        const toggledOptions = toggleActive(options, isOptionDeactivated);
        setValue(optionsFormName, toggledOptions);
    }, [isOptionDeactivated, optionsFormName, getValues, setValue]);
    /** Handles removing nested options from the current option. */
    const removeTree = useCallback(() => {
        setIsOptionsExpanded(false);
        remove();
    }, [remove]);
    // Handles validating the option when options are removed.
    useUpdateEffect(() => {
        if (!hasOptions) {
            trigger(optionFormName);
        }
    }, [hasOptions]);
    // Handles deactivating the parent when all its children are deactivated.
    useUpdateEffect(() => {
        if (parentName && isOptionDeactivated) {
            const siblings = getValues(`${parentName}.${optionsKey}`);
            const isAllSiblingsDeactivated = siblings
                .filter(({ label, value }) => !!label && !!value)
                .every(({ is_deactivated }) => is_deactivated);
            if (isAllSiblingsDeactivated) {
                setValue(`${parentName}.is_deactivated`, true, {
                    shouldDirty: true,
                    shouldTouch: true,
                });
            }
        }
    }, [isOptionDeactivated, parentName, optionFormName, getValues, setValue]);
    return (_jsxs("div", Object.assign({ ref: rowRef, className: classnames(styles.root, {
            [styles.hasParent]: !!parentName,
            [styles.isDragging]: isDragging,
            [styles.isOver]: isOver && !isLastOption,
        }) }, { children: [_jsxs("div", Object.assign({ ref: optionRef, className: classnames(styles.option, {
                    [styles.nestedError]: !isOptionsValid && !isOptionsExpanded,
                }) }, { children: [_jsxs(FlexBox, Object.assign({ className: styles.flexBox, justifyContent: "space-between" }, { children: [_jsx(FlexBox, { children: hasOptions && (_jsx(IconButton, { variant: "tertiary", icon: isOptionsExpanded ? 'angle-down' : 'angle-right', onClick: () => setIsOptionsExpanded(!isOptionsExpanded) })) }), _jsx(FlexBox, { children: !isLastOption && (_jsx("div", Object.assign({ ref: rowHandleRef, className: styles.handle }, { children: _jsx(Icon, { name: "drag" }) }))) })] })), _jsx(HookFormText, { control: control, name: labelFormName, noAlert: !showAlert, noLabel: true, rules: {
                            required: {
                                value: isInputRequired,
                                message: 'The label is required',
                            },
                        }, componentProps: {
                            placeholder: `Option ${fieldIndex + 1}`,
                            onBlur: (event) => handleBlur('label', event),
                            onFocus: (event) => handleFocus('value', event),
                            onInput: (event) => handleInput('label', event),
                        } }), _jsx(HookFormText, { control: control, name: valueFormName, noAlert: !showAlert, noLabel: true, rules: !isOptionFromEndpoint
                            ? {
                                required: {
                                    value: isInputRequired,
                                    message: 'The value is required',
                                },
                                validate: {
                                    conflictingValue: (value) => {
                                        var _a;
                                        const conflictingOptions = convertTreeToFlatItems((_a = getValues('options')) !== null && _a !== void 0 ? _a : [], {
                                            childrenKey: 'options',
                                        }).filter((flatOption) => flatOption.id !== optionId &&
                                            flatOption.value &&
                                            flatOption.value === value);
                                        return (conflictingOptions.length === 0 ||
                                            `Value is already in use`);
                                    },
                                },
                            }
                            : undefined, componentProps: {
                            placeholder: `option_${fieldIndex + 1}`,
                            readOnly: isOptionFromEndpoint,
                            onBlur: (event) => handleBlur('value', event),
                            onFocus: (event) => handleFocus('value', event),
                            onInput: (event) => handleInput('value', event),
                        } }), _jsx(FlexBox, Object.assign({ className: styles.flexBox }, { children: !isLastOption && (_jsx(Tooltip, Object.assign({ dark: true, content: isOptionFromEndpoint
                                ? 'Previously created option can not be removed'
                                : hasOptions
                                    ? 'Remove option and all nested options'
                                    : 'Remove option' }, { children: _jsx(IconButton, { icon: "minus", variant: "secondary", disabled: isOptionFromEndpoint, onClick: () => onRemove(fieldIndex) }) }))) })), _jsx(FlexBox, Object.assign({ className: styles.flexBox }, { children: !isLastOption && (_jsx(_Fragment, { children: hasOptions ? (_jsx(Tooltip, Object.assign({ dark: true, content: hasOptionsFromEndpoint
                                    ? 'Previously created nested options can not be removed'
                                    : 'Remove all nested options' }, { children: _jsx(IconButton, { icon: "close", variant: "secondary", disabled: hasOptionsFromEndpoint, onClick: () => removeTree() }) }))) : (_jsx(Tooltip, Object.assign({ dark: true, content: "Add nested options" }, { children: _jsx(IconButton, { icon: "dropdown-list", variant: "secondary", onClick: () => createTree() }) }))) })) })), _jsx(FlexBox, Object.assign({ className: styles.flexBox }, { children: !isLastOption && (_jsx(HookFormSwitch, { control: control, name: deactivatedFormName, transformValue: (value) => !value, componentProps: {
                                disabled: isParentDeactivated,
                                onChange: () => trigger(optionsKey),
                            } })) }))] })), hasOptions && isOptionsExpanded && (_jsx(Box, Object.assign({ className: styles.options }, { children: _jsx(Options, { parentName: optionFormName, parentId: optionId }) })))] })));
});
/** Returns recursively toggled active state of the option and its children. */
function toggleActive(options, deactivate) {
    return options.map((option) => (Object.assign(Object.assign(Object.assign({}, option), { is_deactivated: deactivate }), (option.options
        ? { options: toggleActive(option.options, deactivate) }
        : {}))));
}
